import { ExtractedRecord, RecordSummaryPayload, TargetSchema } from "@/types";

import {
  claimantDedupKey,
  deduplicateRecordsByKey,
  detectReportedYTD,
  getRecordSchemas,
  getRecordValidationErrors,
} from "./recordUtils";

/**
 * Per-schema aggregates for the review surface (#6755).
 *
 * The server now computes these in the worker at finalize time and puts them on
 * `processing_metadata.record_summary`, because deriving them here required
 * holding every extracted record — and fetching 497,711 records to render a tab
 * badge kills mgmt-api (#6826).
 *
 * The client-side implementation below is retained as the fallback for
 * transformations written before that change, and as the reference the server
 * implementation is tested against. `SummarizeRecords` in
 * apps/pkg/domains/document_processing/types.go must agree with it; both sides
 * have tests over identical fixtures so drift fails in CI rather than showing a
 * reviewer a wrong count.
 */
export type RecordSummary = RecordSummaryPayload;

/**
 * Group records into the per-schema buckets the review tabs render.
 *
 * A record lands in every schema it has a field for, so a claims-register row
 * carrying both claimant and claim_item fields appears in both buckets — which
 * is why `total_records` double-counts it.
 *
 * Claimant records are deduplicated to first-occurrence on `claimantDedupKey`
 * (member_id, falling back to claimant_name plus the household segments),
 * because each spreadsheet row repeats the claimant once per claim line.
 */
export const groupRecordsBySchema = (
  records: ExtractedRecord[] | undefined
): Record<TargetSchema, ExtractedRecord[]> => {
  const bySchema = {} as Record<TargetSchema, ExtractedRecord[]>;
  if (!records) return bySchema;

  for (const record of records) {
    for (const schema of getRecordSchemas(record)) {
      if (!bySchema[schema]) bySchema[schema] = [];
      bySchema[schema].push(record);
    }
  }

  if (bySchema[TargetSchema.CLAIMANT]) {
    bySchema[TargetSchema.CLAIMANT] = deduplicateRecordsByKey(bySchema[TargetSchema.CLAIMANT], claimantDedupKey);
  }

  return bySchema;
};

/**
 * Compute the summary from a COMPLETE record set.
 *
 * Only valid when every record is in hand — it is the fallback path for
 * documents small enough to fetch whole, and the reference implementation the
 * Go side is pinned against. For a windowed read, use the server's summary.
 */
export const summarizeRecords = (records: ExtractedRecord[] | undefined): RecordSummary => {
  const bySchema = groupRecordsBySchema(records);

  const counts: Partial<Record<TargetSchema, number>> = {};
  let total = 0;

  for (const [schema, bucket] of Object.entries(bySchema) as [TargetSchema, ExtractedRecord[]][]) {
    counts[schema] = bucket.length;
    total += bucket.length;
  }

  // DISTINCT records with errors, over the raw record list — NOT a per-bucket
  // sum like total_records (#6755 review round 2). This figure reaches the
  // reviewer as "N records with errors" and gates approval; a two-schema row
  // counted twice tells them to go find errors that don't exist. Mirrors the
  // Go SummarizeRecords semantics exactly.
  //
  // NO extracted_fields PRECONDITION. There was one on both sides, and it made
  // this figure disagree with the server's ?errored=true filter (!4681
  // review): a record whose every mapping failed is KEPT by the transformer
  // and then stamped insufficient_data by the validator, so it has validation
  // errors and no extracted fields. The endpoint returns it; this used to skip
  // it, and the summary reported a smaller number than the endpoint served for
  // the same document.
  //
  // The precondition still applies to the SCHEMA BUCKETS above, where it
  // belongs — such a record has no schemas and so appears on no tab.
  const withErrors = (records ?? []).filter((r) => getRecordValidationErrors(r).length > 0).length;

  return {
    counts_by_schema: counts,
    total_records: total,
    records_with_errors: withErrors,
    max_rows_per_claimant: MAX_ROWS_PER_CLAIMANT_NOT_COMPUTED,
    // Computed, NOT abstained — unlike max_rows_per_claimant above (#6903).
    // The predicate is presence plus a numeric comparison with no name
    // normalizer behind it, so the mirror can match the Go side exactly.
    //
    // Why compute rather than abstain: on a COMPLETE record set this is a real
    // determination, and honouring it keeps the paid-through control correctly
    // inert on a document that genuinely maps no YTD. Abstaining would fail
    // open there and offer a control for a value that does not exist.
    //
    // The caller is responsible for only reaching this with a complete set.
    // useReviewAsOfState checks `recordsPartial` first and treats a cut page as
    // UNKNOWN rather than calling in here — see !4677 review
    // `[AEM-mirror-partial]`, which is the bug that arises when it does not.
    //
    // An earlier version of this comment justified computing by saying
    // abstention would warn "this document maps a carrier YTD total" about a
    // document mapping nothing. That cost no longer exists: the same change
    // regated `isMissingForYtd` on `hasMappedYtd` rather than `showAsOf`, so
    // the fail-open branch asserts nothing about YTD (found in review).
    reported_ytd: detectReportedYTD(records ?? []),
  };
};

/**
 * max_rows_per_claimant is SERVER-COMPUTED ONLY; this mirror abstains.
 *
 * The value gates whether a WINDOWED review projection is safe, and its
 * identity is the projection's own merge key (Go ClaimantWindowIdentity:
 * normalized name | dob | trimmed/lowered member_id, SSN-shaped dropped —
 * shared with mergeClaimantRecords by construction). That name normalizer is
 * not mirrored in TS, and a near-miss reimplementation here would be a guess
 * wearing the costume of a determination — the exact drift the #6755 review
 * flagged when this file computed it on the raw display key.
 *
 * Abstaining is safe because nothing gates on a client-computed value: the
 * window gate (projectionGate.ts) reads only the transformation row's server
 * summary and fails closed when it is absent. NULL, not 0 (#6755 review round
 * 2): the server's 0 is a real, load-bearing fact — "no claimants at all, any
 * window trivially safe" — so abstention must be structurally distinct from
 * it, the same reason `total` grew a totalKnown flag instead of overloading 0.
 * The `=== 1` gates fail closed on null with no further change.
 */
const MAX_ROWS_PER_CLAIMANT_NOT_COMPUTED = null;
